import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsNativeTablet } from "@/hooks/useIsNativeTablet";
import { ReactNode } from "react";

interface CardBaseProps {
  onClick?: () => void;
  color: string;
  animation: string;
  children: ReactNode;
  disabled?: boolean;
  width: string;
}

const CardBase = ({
  onClick,
  color,
  animation,
  children,
  width,
  disabled = false,
}: CardBaseProps) => {
  const isMobile = useIsMobile();
  const isNativeTablet = useIsNativeTablet();
  const cardHeight = isMobile
    ? "h-[84px]"
    : isNativeTablet
      ? "h-[clamp(100px,12dvh,140px)]"
      : "h-[105px]";

  const getBorderColor = (colorClass: string): string => {
    const hexMatch = colorClass.match(/from-\[#([A-Fa-f0-9]{6})\]/);
    if (hexMatch) {
      return `#${hexMatch[1]}`;
    }
    const tailwindMatch = colorClass.match(/from-(\w+-\d+)/);
    if (tailwindMatch) {
      const colorMap: { [key: string]: string } = {
        "blue-500": "#3b82f6",
        "blue-600": "#2563eb",
        "green-500": "#22c55e",
        "green-600": "#16a34a",
        "purple-500": "#a855f7",
        "purple-600": "#9333ea",
        "orange-500": "#f97316",
        "orange-600": "#ea580c",
        "red-500": "#ef4444",
        "red-600": "#dc2626",
        "yellow-500": "#eab308",
        "yellow-600": "#ca8a04",
        "pink-500": "#ec4899",
        "pink-600": "#db2777",
        "indigo-500": "#6366f1",
        "indigo-600": "#4f46e5",
        "teal-500": "#14b8a6",
        "teal-600": "#0d9488",
      };
      return colorMap[tailwindMatch[1]] || "#3b82f6";
    }
    return "#3b82f6";
  };

  const borderColor = getBorderColor(color);

  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const hoverGlow = isMobile
    ? `0 0 20px ${hexToRgba(borderColor, 0.5)}, 0 0 35px ${hexToRgba(
        borderColor,
        0.3,
      )}`
    : `0 0 25px ${hexToRgba(borderColor, 0.5)}, 0 0 50px ${hexToRgba(
        borderColor,
        0.3,
      )}`;

  const elevationShadow = disabled
    ? "0 1px 2px 0 rgb(0 0 0 / 0.05)"
    : "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1), 0 4px 6px -1px rgb(0 0 0 / 0.08)";

  const hoverElevationShadow =
    "0 4px 6px -1px rgb(0 0 0 / 0.12), 0 2px 4px -2px rgb(0 0 0 / 0.1), 0 10px 15px -3px rgb(0 0 0 / 0.08)";

  return (
    <Card
      className={`border border-primary/20 transition-colors duration-150 cursor-pointer overflow-hidden relative group 
        ${disabled ? "cursor-not-allowed" : "cursor-pointer"} 
        ${cardHeight} 
        ${isMobile ? width : width} 
        ${animation}`}
      style={{
        boxShadow: elevationShadow,
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = `${hoverElevationShadow}, ${hoverGlow}`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = elevationShadow;
      }}
      onClick={disabled ? undefined : onClick}
    >
      <div className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0`} />
      {disabled && (
        <div className="absolute inset-0 bg-primary/20 z-30 rounded-lg" />
      )}
      <CardContent
        className={`${
          isMobile ? "p-2" : "p-3"
        } relative z-10 h-full flex flex-col justify-between`}
      >
        {children}
      </CardContent>
    </Card>
  );
};

export default CardBase;
